import type { CoachInviteContext } from '@/types/wizard'

/**
 * The coach invite an athlete arrived on, held across the signup detour.
 *
 * A not-yet-registered athlete opening `/invite/<uuid>` is bounced to
 * `/login?next=...` by RequireAuth, and from there may go on to `/register`,
 * email verification and onboarding before they are ever authenticated enough
 * to accept. A `next` query param does not survive that: `RegisterPage` does
 * not read it, and registration navigates onward by its own logic.
 *
 * So the uuid is stashed here on the way past, exactly as the iOS app holds a
 * pending destination across its own auth and onboarding gates, and consumed
 * once onboarding can act on it.
 *
 * localStorage, not sessionStorage: email verification can land the athlete in
 * a NEW tab, which starts with an empty sessionStorage and would silently lose
 * the invite.
 */
const KEY = 'beoflow_pending_coach_invite'

/**
 * The two consent-invite paths worth remembering.
 *
 * `/r/consent-invite/<uuid>` is the TYPED destination route, and it is what the
 * coach's "Generate invite link" actually emits today — matching only the
 * legacy `/invite/<uuid>` meant the stash never fired for a single real invite.
 * Both are routed to `InviteAcceptPage` in App.tsx, so both must be recognised
 * here. `team-invite` and `plan-editor-invite` are deliberately excluded: they
 * are a different relationship and must not pre-seed the athlete wizard.
 */
const INVITE_PATH = /^\/(?:invite|r\/consent-invite)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i

export function inviteUuidFromPath(pathname: string): string | null {
  return INVITE_PATH.exec(pathname)?.[1] ?? null
}

/** The same uuid shape, standalone. The native app passes the invite as a query
 *  param rather than a path, and an unvalidated value there would stash junk
 *  and pre-seed the wizard's coach answer on no evidence at all. */
export function isInviteUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
}

export function rememberCoachInvite(invite: CoachInviteContext): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(invite))
  } catch {
    /* private mode / quota — the invite link still works, it just won't pre-seed */
  }
}

/** Remember only the uuid; the rest is filled in once we can read the invite. */
/** Mark the stashed invite as ACCEPTED — the only state that may pre-seed the
 *  wizard. Until then the athlete has consented to nothing, and seeding would
 *  tell Flow they have a coach on the strength of a clicked link. */
export function markCoachInviteAccepted(): void {
  const invite = readCoachInvite()
  if (invite) rememberCoachInvite({ ...invite, accepted: true })
}

/** The accepted invite, or null. This is what the wizard seeds from. */
export function readAcceptedCoachInvite(): CoachInviteContext | null {
  const invite = readCoachInvite()
  return invite?.accepted ? invite : null
}

export function rememberCoachInviteUuid(inviteUuid: string): void {
  rememberCoachInvite({
    inviteUuid,
    coachProfileId: null,
    coachName: null,
    groupUuid: null,
    planId: null,
    accepted: false,
  })
}

export function readCoachInvite(): CoachInviteContext | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CoachInviteContext>
    // A stash without a uuid identifies nothing and would pre-seed the wizard
    // on no evidence at all.
    return typeof parsed?.inviteUuid === 'string' && parsed.inviteUuid
      ? {
          inviteUuid: parsed.inviteUuid,
          coachProfileId: parsed.coachProfileId ?? null,
          coachName: parsed.coachName ?? null,
          groupUuid: parsed.groupUuid ?? null,
          planId: parsed.planId ?? null,
          accepted: parsed.accepted === true,
        }
      : null
  } catch {
    return null
  }
}

export function clearCoachInvite(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* nothing to do */
  }
}
